import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, filter, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { SurveyService } from '@ps/survey-data-access';
import { Survey, SurveyKind } from '@ps/survey-utils';
import { fromOption, Nullable } from '@ps/utils';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from './survey-creation-core.actions';
import {
  selectRootSurvey,
  selectSurvey,
} from './survey-creation-core.selectors';
import {
  activeProjectChildKey,
  firstLoadedChildId,
  isLoadedCoreSibling,
  registeredSurveys,
  rootSurveyOf,
} from './utils/core-children.helper';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { surveyCreationFeature } from '../survey-creation.feature';
import { SurveyCreationAudienceActions } from '../audience/survey-creation-audience.actions';
import { SurveyCreationLaunchActions } from '../launch/survey-creation-launch.actions';

@Injectable()
export class SurveyCreationCoreEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyService = inject(SurveyService);

  // Cross-listens to both stage-specific init actions — the survey has to
  // load regardless of which page opened it. See
  // SurveyCreationAudienceActions.init's doc comment.
  public readonly loadRootSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.init,
        SurveyCreationLaunchActions.init,
      ),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => [this._store.select(selectRootSurvey)]),
      // Idempotence guard: skip the fetch when this exact survey is already in
      // memory, unless forceReload asks for a fresh copy. Compared against the
      // root, not the active child: a project's active child is a different
      // survey than the id `init` carries.
      filter(
        ([{ surveyId, forceReload }, rootSurvey]) =>
          forceReload || !rootSurvey || rootSurvey.id !== surveyId,
      ),
      switchMap(([action]) =>
        this._surveyService.getByIdTargeting(action.surveyId).pipe(
          map((survey) =>
            SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess({
              survey,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationCoreApiActions.loadRootSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Legacy: a Traditional MC locale switch on the launch page re-GETs the target
  // instead of rendering its sibling-list record. A cold or forced load fetches above.
  public readonly refreshTraditionalLocale$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.init),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      filter(
        ([{ surveyId, forceReload }, survey]) =>
          !forceReload &&
          !!survey &&
          survey.id === surveyId &&
          SurveyKind.isTraditionalMc(survey),
      ),
      switchMap(([{ surveyId }]) =>
        Option.from(surveyId).mapOr(EMPTY, (id) =>
          this._surveyService.getByIdTargeting(id).pipe(
            map((survey) =>
              SurveyCreationCoreApiActions.localeSurveyRefreshed({ survey }),
            ),
            this.psCatchError(() => EMPTY, { notify: true }),
          ),
        ),
      ),
    ),
  );

  // Load root -> child surveys (if needed) -> active child key -> active child.
  // Here: a survey with no family to fetch, or whose family is already loaded
  // (a sibling re-init), moves straight to its active survey.
  public readonly selectActiveSurveyAfterRootLoad$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      concatLatestFrom(() =>
        this._store.select(surveyCreationFeature.selectCore),
      ),
      filter(
        ([{ survey }, core]) =>
          SurveyKind.childFilter(survey).isNone() ||
          isLoadedCoreSibling(core.children, survey.id),
      ),
      map(([{ survey }, core]) =>
        _activeSurveyAction(
          survey,
          core.activeChildKey,
          registeredSurveys(core.children),
        ),
      ),
    ),
  );

  // Only a loaded family moves on; a failed one leaves the active survey unloaded.
  // Without a child picked yet, a project takes the first loaded child.
  public readonly selectActiveChildAfterFamilyLoad$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess),
      concatLatestFrom(() =>
        this._store.select(surveyCreationFeature.selectCore),
      ),
      mergeMap(([{ surveys }, core]) =>
        fromOption(
          rootSurveyOf(core).map((survey) =>
            _activeSurveyAction(survey, core.activeChildKey, surveys),
          ),
        ),
      ),
    ),
  );

  // Only a project's active child is a survey of its own; any other kind's
  // active survey is the root. A newer key cancels an in-flight fetch.
  public readonly loadActiveChildSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.setActiveChildKey),
      concatLatestFrom(() =>
        this._store.select(surveyCreationFeature.selectCore),
      ),
      switchMap(([{ activeChildKey }, core]) =>
        rootSurveyOf(core)
          .andThen((survey) => activeProjectChildKey(survey, activeChildKey))
          .mapOr(EMPTY, (surveyId) => this._loadActiveChildSurvey$(surveyId)),
      ),
    ),
  );

  private _loadActiveChildSurvey$(surveyId: number): Observable<Action> {
    return this._surveyService.getByIdTargeting(surveyId, true).pipe(
      map((survey) =>
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess({ survey }),
      ),
      this.psCatchError((error) =>
        of(
          SurveyCreationCoreApiActions.loadActiveSurveyForEditFailure({
            error: error.message,
          }),
        ),
      ),
    );
  }
}

// A project hands off to its active child, else its first loaded child; any
// other kind is its own active survey.
function _activeSurveyAction(
  survey: Survey,
  activeChildKey: Nullable<SurveyChildKey>,
  loadedSurveys: readonly Survey[],
): Action {
  return SurveyKind.isProject(survey)
    ? SurveyCreationCoreActions.setActiveChildKey({
        activeChildKey: activeProjectChildKey(survey, activeChildKey)
          .orElse(() => firstLoadedChildId(loadedSurveys))
          .mapOr(activeChildKey, (childId) => childId),
      })
    : SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess({ survey });
}
